import { useVirtualizer } from '@tanstack/react-virtual';
import { Command as CommandPrimitive } from 'cmdk';
import * as React from 'react';

import { cn } from '@/lib/utils';

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
    return;
  }
  if (ref && 'current' in ref) {
    (ref as { current: T | null }).current = value;
  }
}

const VirtualCommandList = React.forwardRef<
  React.ElementRef<typeof CommandPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof CommandPrimitive.List> & {
    itemHeight?: number;
    overscan?: number;
  }
>(({ className, children, itemHeight = 40, overscan = 5, ...props }, ref) => {
  const listRef = React.useRef<HTMLElement | null>(null);
  const pendingSelectIndexRef = React.useRef<number | undefined>(undefined);
  const childrenArray = React.Children.toArray(children);
  const itemCount = childrenArray.length;

  const virtualizer = useVirtualizer({
    count: itemCount,
    getScrollElement: () => listRef.current,
    estimateSize: () => itemHeight,
    overscan,
  });

  const setListRef = React.useCallback(
    (node: HTMLElement | null) => {
      listRef.current = node;
      assignRef(ref, node);
    },
    [ref]
  );

  const selectIndex = React.useCallback((index: number) => {
    const list = listRef.current;
    if (!list) return;

    const item = list.querySelector<HTMLElement>(`[data-virtual-index="${index}"] [cmdk-item]`);
    if (!item) {
      pendingSelectIndexRef.current = index;
      return;
    }

    pendingSelectIndexRef.current = undefined;
    item.dispatchEvent(new PointerEvent('pointermove', { bubbles: true }));
  }, []);

  const virtualItems = virtualizer.getVirtualItems();

  React.useLayoutEffect(() => {
    const pendingIndex = pendingSelectIndexRef.current;
    if (pendingIndex === undefined) return;
    selectIndex(pendingIndex);
  }, [selectIndex, virtualItems]);

  // Keep the highlighted row in view as cmdk moves selection among mounted items.
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const observer = new MutationObserver(() => {
      const selected = list.querySelector<HTMLElement>('[cmdk-item][aria-selected="true"]');
      if (!selected) return;

      const row = selected.closest<HTMLElement>('[data-virtual-index]');
      const index = Number(row?.dataset.virtualIndex);
      if (Number.isNaN(index)) return;

      virtualizer.scrollToIndex(index, { align: 'auto' });
    });

    observer.observe(list, {
      attributes: true,
      attributeFilter: ['aria-selected'],
      subtree: true,
    });

    return () => observer.disconnect();
  }, [virtualizer]);

  // Capture-phase handler on the cmdk root so Arrow keys can move past the
  // currently mounted virtual window (input focus never reaches the list).
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const commandRoot = list.closest<HTMLElement>('[cmdk-root]');
    if (!commandRoot) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (itemCount === 0) return;

      const selected = list.querySelector<HTMLElement>('[cmdk-item][aria-selected="true"]');
      const row = selected?.closest<HTMLElement>('[data-virtual-index]');
      const currentIndex = Number(row?.dataset.virtualIndex);
      if (Number.isNaN(currentIndex) || currentIndex < 0) return;

      const nextIndex =
        event.key === 'ArrowDown'
          ? Math.min(currentIndex + 1, itemCount - 1)
          : Math.max(currentIndex - 1, 0);

      if (nextIndex === currentIndex) return;

      const isNextMounted = virtualizer.getVirtualItems().some(item => item.index === nextIndex);
      if (isNextMounted) return;

      event.preventDefault();
      event.stopPropagation();
      pendingSelectIndexRef.current = nextIndex;
      virtualizer.scrollToIndex(nextIndex, {
        align: event.key === 'ArrowDown' ? 'end' : 'start',
      });
    };

    commandRoot.addEventListener('keydown', onKeyDown, true);
    return () => commandRoot.removeEventListener('keydown', onKeyDown, true);
  }, [itemCount, virtualizer]);

  return (
    <CommandPrimitive.List
      ref={setListRef}
      className={cn('max-h-[300px] min-h-[300px] overflow-y-auto overflow-x-hidden', className)}
      {...props}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map(virtualItem => {
          const child = childrenArray[virtualItem.index];
          if (!child) return null;

          return (
            <div
              key={virtualItem.key}
              data-virtual-index={virtualItem.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${virtualItem.size}px`,
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {child}
            </div>
          );
        })}
      </div>
    </CommandPrimitive.List>
  );
});

VirtualCommandList.displayName = 'VirtualCommandList';

export { VirtualCommandList };
